import "./payrollSettings.css";
import "../../companyDetails.css";
import T from "utils/constants/T";
import {
  StyledBodyTypography,
  StyledTitleTypography,
} from "../CompanyTab/CompanyInfo";

import { useNavigate } from "react-router";
import MainCard from "components/MainCard";
import {
  Button,
  Grid,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Stack,
} from "@mui/material";
import { Check, ContentCopy } from "@mui/icons-material";
import { useSelector } from "store";
import CashDCard from "components/common/CashDCard";
import { maskNumber } from "utils/CommonMethods";
import { useCallback, useState } from "react";

interface ViewPayrollSettingsTimeSiteProProps {
  companyName?: string;
  systemName?: string;
  role?: string;
  employee_cost_source?: string;
}

const ViewField = ({
  title,
  value,
  sm,
  mask = false,
  copyKey = null,
  onCopy,
  copyStatus,
}: any) => {
  const displayValue = value ? (mask ? maskNumber(value) : value) : "-";
  return (
    <Grid item xs={12} sm={sm}>
      <StyledTitleTypography>{title}</StyledTitleTypography>
      {copyKey && value ? (
        <Stack direction="row" alignItems="center" spacing={1}>
          <StyledBodyTypography>{displayValue}</StyledBodyTypography>
          <Tooltip title={copyStatus ? "Copied" : "Copy to clipboard"}>
            <IconButton onClick={() => onCopy(copyKey, value)} size="small">
              <Stack direction="row" alignItems="center" spacing={0.5}>
                {copyStatus ? (
                  <Check fontSize="small" />
                ) : (
                  <ContentCopy fontSize="small" />
                )}
              </Stack>
            </IconButton>
          </Tooltip>
        </Stack>
      ) : (
        <StyledBodyTypography>{displayValue}</StyledBodyTypography>
      )}
    </Grid>
  );
};

const SectionHeader = ({ title, showEditButton, onEdit }: any) => (
  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
    <Typography variant="h5" fontWeight="600" color="primary">
      {title}
    </Typography>
    {showEditButton && (
      <Button variant="contained" className="main-card-button" onClick={onEdit}>
        {T.EDIT}
      </Button>
    )}
  </Box>
);

const ViewPayrollSettingsTimeSitePro = ({
  companyName = "",
  systemName = "",
  employee_cost_source = "TIMESITE_PRO",
}: ViewPayrollSettingsTimeSiteProProps) => {
  const navigate = useNavigate();
  const { role } = useSelector((state) => state.auth) as {
    onBoardedCompanyData: any;
    role: string;
  };
  const { auth_myob, auth_timesite } = useSelector(
    (state: any) => state.companyTabData,
  );
  const currentEmployeeCostSource = employee_cost_source;

  const [copiedFormFieldStatus, setCopiedFormFieldStatus] = useState<
    Record<string, boolean>
  >({ api_key: false, myob_password: false });

  const handleCopy = useCallback((field: string, text: string | undefined) => {
    if (!text) return;
    setCopiedFormFieldStatus((prev) => ({ ...prev, [field]: true }));
    navigator.clipboard.writeText(text);
    setTimeout(
      () => setCopiedFormFieldStatus((prev) => ({ ...prev, [field]: false })),
      2000,
    );
  }, []);

  const showEditButton = role === "superadmin" || role === "employer";
  const handleEdit = (section: string) => () =>
    navigate("/companyDetails/payrollSettings/edit", { state: { section } });

  return (
    <MainCard title={systemName}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <SectionHeader
            title="Employee Cost Source"
            showEditButton={showEditButton}
            onEdit={handleEdit("cost_source")}
          />
          <CashDCard borderRadius="12px">
            <Grid container spacing={2}>
              <ViewField
                title="Employee Cost Source"
                value={
                  currentEmployeeCostSource === "TIMESITE_PRO"
                    ? "Timesite Pro"
                    : "MYOB Payroll"
                }
                sm={12}
              />
            </Grid>
          </CashDCard>
        </Grid>

        <Grid item xs={12}>
          <SectionHeader
            title="MYOB Acumatica Credentials"
            showEditButton={showEditButton}
            onEdit={handleEdit("myob")}
          />
          <CashDCard borderRadius="12px">
            <Grid container spacing={2}>
              <ViewField title="Base URL" value={auth_myob?.baseUrl} sm={4} />
              <ViewField title="User Name" value={auth_myob?.userName} sm={4} />
              <ViewField
                title="User Password"
                value={auth_myob?.userPassword}
                sm={4}
                mask
                copyKey="myob_password"
                onCopy={handleCopy}
                copyStatus={copiedFormFieldStatus.myob_password}
              />
            </Grid>
          </CashDCard>
        </Grid>

        <Grid item xs={12}>
          <SectionHeader
            title="TimeSite Pro Credentials"
            showEditButton={showEditButton}
            onEdit={handleEdit("timesite")}
          />
          <CashDCard borderRadius="12px">
            <Grid container spacing={2}>
              <ViewField
                title="Company Name"
                value={auth_timesite?.companyName}
                sm={3}
              />
              <ViewField
                title="Site Name"
                value={auth_timesite?.siteName}
                sm={3}
              />
              <ViewField
                title="Workspace Name"
                value={auth_timesite?.workspaceName}
                sm={3}
              />
              <ViewField
                title="API Key"
                value={auth_timesite?.apiKey}
                sm={3}
                mask
                copyKey="api_key"
                onCopy={handleCopy}
                copyStatus={copiedFormFieldStatus.api_key}
              />
            </Grid>
          </CashDCard>
        </Grid>
      </Grid>
    </MainCard>
  );
};

export default ViewPayrollSettingsTimeSitePro;
